/**
 * Neighbour-driven block states (shapes.ts): a stair's corner shape and a fence / wall / pane's arms
 * are stored in the block id, so they are settled whenever a chunk is generated and whenever a
 * neighbour changes (ChunkManager.setBlock). See blocks.ts `reshapeId`.
 */

import { CHUNK_SIZE } from '../constants';
import { CHUNK_AREA, CHUNK_VOLUME } from './chunk';
import { NEEDS_RESHAPE, reshapeId } from '../blocks';

/**
 * Settle every shaped block of a freshly generated chunk against its own cells (cells beyond the
 * chunk count as air; `reshapeChunkBorder` revisits the edge once the neighbours exist). Runs in the
 * gen worker before lighting, so converting a pre-family id (nether brick stairs filter no light,
 * the family ids do) is lit correctly.
 */
export function reshapeChunkLocal(blocks: Uint16Array): void {
  for (let i = 0; i < CHUNK_VOLUME; i++) {
    const id = blocks[i];
    if (NEEDS_RESHAPE[id] !== 1) continue;
    const x = i & 15, z = (i >> 4) & 15;
    const nid = reshapeId(id, (dx, dz) => {
      const nx = x + dx, nz = z + dz;
      return nx < 0 || nx >= CHUNK_SIZE || nz < 0 || nz >= CHUNK_SIZE ? 0 : blocks[i + dx + dz * CHUNK_SIZE];
    });
    if (nid !== id) blocks[i] = nid;
  }
}

/** Re-settle the cells on a chunk's edge against the real neighbour chunks. Same-filter swaps only, so no relight. */
export function reshapeChunkBorder(
  blocks: Uint16Array, cx: number, cz: number, getBlock: (wx: number, wy: number, wz: number) => number
): void {
  // `blocks` may be short (chunk.ts): what it leaves out is air.
  // Only the 60 edge cells of each layer are visited, in index order.
  for (let i = 0; i < blocks.length; i++) {
    const x = i & 15, z = (i >> 4) & 15;
    if (x === 1 && z !== 0 && z !== 15) { i += 13; continue; } // skip the interior of the row: next is x = 15
    const id = blocks[i];
    if (NEEDS_RESHAPE[id] !== 1) continue;
    const wy = Math.floor(i / CHUNK_AREA), wx = cx * CHUNK_SIZE + x, wz = cz * CHUNK_SIZE + z;
    const nid = reshapeId(id, (dx, dz) => getBlock(wx + dx, wy, wz + dz));
    if (nid !== id) blocks[i] = nid;
  }
}
